(() => {
  function initTicker(root) {
    if (!root) return;
    const viewport = root.querySelector('.announcement-ticker__viewport');
    const track = root.querySelector('.announcement-ticker__track');
    const toggle = root.querySelector('[data-ticker-toggle]');
    if (!track) return;

    const speed = Number(root.dataset.speed || 30);
    const pauseOnHover = root.dataset.pauseHover === 'true';

    // Reflect duration to CSS var in case the section was rendered dynamically
    track.style.setProperty('--ticker-duration', `${speed}s`);

    // Ensure seamless loop: duplicate items until one sequence >= viewport width,
    // and set shift distance to that sequence's width.
    function prepareLoop() {
      if (!viewport || !track) return;
      const allItems = Array.from(track.querySelectorAll('.announcement-ticker__item'));
      // Identify base (non-duplicate) items as those without aria-hidden
      let baseItems = allItems.filter((li) => !li.hasAttribute('aria-hidden'));
      let duplicateItems = allItems.filter((li) => li.getAttribute('aria-hidden') === 'true');

      // If we don't have duplicates yet (editor re-render), create once
      if (duplicateItems.length === 0 && baseItems.length > 0) {
        const frag = document.createDocumentFragment();
        baseItems.forEach((li) => {
          const clone = li.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          const a = clone.querySelector('a');
          if (a) a.setAttribute('tabindex', '-1');
          frag.appendChild(clone);
        });
        track.appendChild(frag);
        duplicateItems = Array.from(track.querySelectorAll('.announcement-ticker__item[aria-hidden="true"]'));
      }

      // Measure width of one base sequence
      const measureBaseWidth = () => {
        let w = 0;
        baseItems.forEach((li) => { w += li.getBoundingClientRect().width; });
        // Add the CSS gap between items: approximate using computed style
        const cs = getComputedStyle(track);
        const gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
        if (baseItems.length > 1) w += gap * (baseItems.length - 1);
        return Math.ceil(w);
      };

      let baseWidth = measureBaseWidth();
      const viewportWidth = Math.ceil(viewport.getBoundingClientRect().width);

      // If base sequence is narrower than viewport, repeat base items until it exceeds viewport
      if (baseWidth < viewportWidth && baseItems.length) {
        const frag = document.createDocumentFragment();
        while (baseWidth < viewportWidth) {
          baseItems.forEach((li) => {
            const clone = li.cloneNode(true);
            frag.appendChild(clone);
          });
          // Temporarily append to measure
          track.appendChild(frag);
          // Refresh baseItems as the newly appended are considered part of base
          baseItems = Array.from(track.querySelectorAll('.announcement-ticker__item:not([aria-hidden])'));
          baseWidth = measureBaseWidth();
        }
        // Rebuild the duplicate half to mirror the new base
        track.querySelectorAll('.announcement-ticker__item[aria-hidden="true"]').forEach((el) => el.remove());
        const mirrorFrag = document.createDocumentFragment();
        baseItems.forEach((li) => {
          const clone = li.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          const a = clone.querySelector('a');
          if (a) a.setAttribute('tabindex', '-1');
          mirrorFrag.appendChild(clone);
        });
        track.appendChild(mirrorFrag);
      }

      // Set shift distance (px) for animation
      baseWidth = measureBaseWidth();
      track.style.setProperty('--ticker-shift', `${baseWidth}px`);
    }

    prepareLoop();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function setPaused(paused) {
      if (!track) return;
      track.classList.toggle('is-paused', !!paused);
      if (toggle) {
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        const play = toggle.querySelector('[data-label-play]');
        const pause = toggle.querySelector('[data-label-pause]');
        // Keep labels hidden visually but update aria-label for SR
        const playLabel = play?.textContent?.trim() || 'Play';
        const pauseLabel = pause?.textContent?.trim() || 'Pause';
        toggle.setAttribute('aria-label', paused ? playLabel : pauseLabel);
      }
    }

    // Start with playing unless reduced motion
    setPaused(reducedMotion.matches);

    if (toggle) {
      toggle.addEventListener('click', () => {
        const paused = track.classList.contains('is-paused');
        setPaused(!paused);
      });
    }

    if (pauseOnHover && viewport) {
      viewport.addEventListener('mouseenter', () => setPaused(true));
      viewport.addEventListener('mouseleave', () => setPaused(reducedMotion.matches));
      viewport.addEventListener('focusin', () => setPaused(true));
      viewport.addEventListener('focusout', () => setPaused(reducedMotion.matches));
    }

    reducedMotion.addEventListener('change', (e) => setPaused(e.matches));

    // Recompute on resize to keep loop seamless
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => prepareLoop(), 150);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.announcement-ticker__inner').forEach(initTicker);
  });

  // Theme Editor support (Shopify design mode)
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target?.querySelector?.('.announcement-ticker__inner');
    if (el) initTicker(el);
  });
})();
